import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { WarehouseResponse, WarehouseTypeService } from '../../../services/warehouse-type';
import { AccountTypeService } from '../../../services/account-type';
import { AccountType } from '../../../enum/account-type.enum';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly superAdminPages = ['0', '1', '2', '3', '4', '5', '6', '7'];
  private tokenSubject = new BehaviorSubject<string | null>(
    localStorage.getItem('token')
  );
  token$ = this.tokenSubject.asObservable();
  constructor(
    private _HttpClient: HttpClient,
    private _Route: Router,
    private warehouseService: WarehouseTypeService,
    private accountTypeService: AccountTypeService
  ) {}

  user_login(person: any): Observable<any> {
    this.flowLog('login api request', { username: person?.username });
    return this._HttpClient.post(`${environment.Server_URL}/login`, person);
  }

  setToken(token: string): void {
    this.flowLog('set token', { tokenPrefix: token?.slice(0, 8) });
    localStorage.setItem('token', token);
    this.tokenSubject.next(token);
  }

  private storeToken(token: string): void {
    this.flowLog('store token without emission', { tokenPrefix: token?.slice(0, 8) });
    localStorage.setItem('token', token);
  }

  private publishStoredToken(token: string): void {
    this.flowLog('publish stored token', { tokenPrefix: token?.slice(0, 8) });
    this.tokenSubject.next(token);
  }

  clearToken(): void {
    this.flowLog('clear token');
    localStorage.removeItem('token');
    this.tokenSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  initializeAuthenticatedSession(res: any): Observable<WarehouseResponse | null> {
    const user = res?.user ?? {};
    const token = res?.token;
    this.flowLog('initialize session start', {
      username: user?.username,
      responseWarehouseId: user?.warehouse_id,
      responseUserType: user?.type,
      tokenPrefix: token?.slice(0, 8)
    });

    if (!token) {
      this.resetAuthState();
      return throwError(() => new Error('Login response did not include a token.'));
    }

    const userType = this.getUserTypeFromLoginResponse(user, res);
    const accessPages = this.getAccessPagesFromLoginResponse(user, res, userType);
    const warehouseId = this.getWarehouseIdFromLoginResponse(user, res);
    this.flowLog('parsed login response', { userType, accessPages, warehouseId });

    this.resetAuthState();

    if (user?.username) {
      localStorage.setItem('username', user.username);
    }

    this.accountTypeService.setAccountContext(userType, accessPages);

    if (warehouseId) {
      localStorage.setItem('posId', String(warehouseId));
    }

    this.storeToken(token);
    this.flowLog('session state stored before warehouse load', { warehouseId });

    if (!warehouseId) {
      this.flowLog('initialize session no warehouse id');
      this.publishStoredToken(token);
      return of(null);
    }

    return this.warehouseService.getWarehouseType(warehouseId).pipe(
      tap((warehouse) => this.flowLog('initialize session warehouse loaded', {
        warehouseId,
        warehousesType: warehouse?.warehouses_type,
        responseWarehouseId: warehouse?.warehouse?.id
      })),
      catchError((error) => {
        console.error('Warehouse initialization failed:', error);
        this.flowLog('initialize session warehouse error', { message: error?.message, status: error?.status });
        this.warehouseService.resetWarehouseContext();
        return of(null);
      }),
      tap(() => this.publishStoredToken(token))
    );
  }

  initializeStoredSession(): Observable<WarehouseResponse | null> {
    const token = this.getToken();
    const warehouseId = Number(localStorage.getItem('posId'));
    this.flowLog('initialize stored session start', { warehouseId, hasToken: !!token });

    if (!token || !Number.isFinite(warehouseId) || warehouseId <= 0) {
      this.flowLog('initialize stored session skipped');
      return of(null);
    }

    return this.warehouseService.getWarehouseType(warehouseId).pipe(
      tap((warehouse) => this.flowLog('initialize stored session warehouse loaded', {
        warehouseId,
        warehousesType: warehouse?.warehouses_type,
        responseWarehouseId: warehouse?.warehouse?.id
      })),
      catchError((error) => {
        console.error('Stored warehouse initialization failed:', error);
        this.flowLog('initialize stored session warehouse error', { message: error?.message, status: error?.status });
        this.warehouseService.resetWarehouseContext();
        return of(null);
      })
    );
  }

  resetAuthState(): void {
    this.flowLog('reset auth state start');
    this.clearToken();
    localStorage.removeItem('posId');
    localStorage.removeItem('username');
    localStorage.removeItem('user_type');
    localStorage.removeItem('access_pages');
    localStorage.removeItem('cart');
    localStorage.removeItem('cartWarehouseId');
    this.accountTypeService.clearStorage();
    this.warehouseService.resetWarehouseContext();
    this.flowLog('reset auth state done');
  }

  private getWarehouseIdFromLoginResponse(user: any, res: any): number | null {
    const warehouseId = Number(
      user?.warehouse_id ??
      user?.posId ??
      user?.pos_id ??
      user?.warehouse?.id ??
      res?.warehouse_id ??
      res?.posId ??
      res?.pos_id ??
      res?.warehouse?.id
    );

    return Number.isFinite(warehouseId) && warehouseId > 0 ? warehouseId : null;
  }

  private getUserTypeFromLoginResponse(user: any, res: any): number {
    const userType = Number(user?.type ?? res?.user_type ?? res?.type);

    return Number.isFinite(userType) && userType > 0 ? userType : 0;
  }

  private getAccessPagesFromLoginResponse(user: any, res: any, userType: number): string[] {
    if (userType === AccountType.SuperAdmin) {
      return this.superAdminPages;
    }

    return this.normalizeAccessPages(
      user?.access_pages ?? res?.access_pages ?? res?.accessPages
    );
  }

  private normalizeAccessPages(rawPages: any): string[] {
    let pages = rawPages;

    if (typeof pages === 'string') {
      try {
        pages = JSON.parse(pages);
      } catch {
        pages = pages
          .split(',')
          .map((page: string) => page.trim())
          .filter(Boolean);
      }
    }

    if (!Array.isArray(pages)) {
      return [];
    }

    return pages.map((page) => String(page));
  }

  private flowLog(step: string, extra: Record<string, any> = {}): void {}
}
